import { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useSelector, useDispatch } from 'react-redux';
import {
  HiOutlineUpload,
  HiOutlineDocumentText,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineExclamation,
  HiOutlineCheck,
  HiOutlineSearch,
  HiOutlinePencil,
  HiOutlineX,
  HiOutlineDownload,
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineSortAscending,
  HiOutlineSortDescending,
} from 'react-icons/hi';
import { AiOutlineShoppingCart, AiOutlineBarcode } from 'react-icons/ai';
import { BiPackage } from 'react-icons/bi';
import { updateOrganizationSettingsAction } from '../redux/actions/organisationActions';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { DeleteDialog } from '../components/shared/DeleteDialog';
import './ProductCatalog.css';

// Helper to generate unique ID
const generateUniqueId = () => {
  return `prod_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// Format currency
const formatCurrency = (amount, currency = 'BGN') => {
  return new Intl.NumberFormat('bg-BG', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

const ProductCatalog = () => {
  // State for products
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // UI State
  const [activeTab, setActiveTab] = useState('catalog');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'productNumber', direction: 'asc' });
  
  // Import State
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [headerRow, setHeaderRow] = useState([]);
  const [showColumnMapper, setShowColumnMapper] = useState(false);
  const [columnMapping, setColumnMapping] = useState({
    productNumber: -1,
    productName: -1,
    price: -1,
    category: -1,
  });
  
  // Manual Entry State
  const [manualEntry, setManualEntry] = useState({
    productNumber: '',
    productName: '',
    price: '',
    category: '',
  });
  const [editingProduct, setEditingProduct] = useState(null);

  // Multi-select State
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  // Status State
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Refs
  const fileInputRef = useRef(null);
  
  // Redux
  const dispatch = useDispatch();
  const currentOrganisation = useSelector((state) => state.organisation?.currentOrganisation);
  const isAdmin = useIsAdmin();

  // Load products from organization settings
  useEffect(() => {
    if (currentOrganisation?.settings?.productCatalog) {
      setProducts(currentOrganisation.settings.productCatalog);
      setSelectedIds(new Set());
    }
  }, [currentOrganisation]);

  // Filter and sort products
  useEffect(() => {
    let result = [...products];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(product => 
        product.productNumber?.toLowerCase().includes(query) ||
        product.productName?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      if (sortConfig.key === 'price') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredProducts(result);
  }, [products, searchQuery, sortConfig]);

  // Save products to organization
  const saveProductsToOrganization = async (newProducts) => {
    if (!currentOrganisation) return;
    
    try {
      setLoading(true);
      await dispatch(updateOrganizationSettingsAction(currentOrganisation._id, {
        productCatalog: newProducts
      }));
      showSuccessMessage('Item catalog saved successfully');
    } catch (error) {
      setError('Failed to save item catalog. Please try again.');
      console.error('Failed to save products:', error);
    } finally {
      setLoading(false);
    }
  };

  const showSuccessMessage = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  // File handling
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setSuccess('');
    setPreviewData(null);
    setHeaderRow([]);
    setShowColumnMapper(false);
    
    previewFile(selectedFile);
  };

  const previewFile = (selectedFile) => {
    setLoading(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length === 0) {
          setError('No data found in the file');
          setLoading(false);
          return;
        }

        const headers = jsonData[0];
        const previewRows = jsonData.slice(1, Math.min(6, jsonData.length));
        
        setHeaderRow(headers);
        setPreviewData({ headers, rows: previewRows });
        
        // Auto-detect columns
        const mapping = { ...columnMapping };
        headers.forEach((header, index) => {
          const headerText = String(header).toLowerCase();
          
          if (headerText.includes('number') || headerText.includes('sku') || headerText.includes('code') || headerText.includes('номер') || headerText.includes('артикул')) {
            mapping.productNumber = index;
          } else if (headerText.includes('name') || headerText.includes('product') || headerText.includes('title') || headerText.includes('име') || headerText.includes('наименование') || headerText.includes('продукт')) {
            mapping.productName = index;
          } else if (headerText.includes('price') || headerText.includes('cost') || headerText.includes('цена') || headerText.includes('стойност')) {
            mapping.price = index;
          } else if (headerText.includes('category') || headerText.includes('type') || headerText.includes('категория') || headerText.includes('тип')) {
            mapping.category = index;
          }
        });
        
        setColumnMapping(mapping);
        setShowColumnMapper(true);
        
      } catch (error) {
        console.error('Error processing file:', error);
        setError('Error processing file. Please make sure it is a valid Excel/CSV file.');
      } finally {
        setLoading(false);
      }
    };

    reader.onerror = () => {
      setError('Error reading file');
      setLoading(false);
    };

    reader.readAsArrayBuffer(selectedFile);
  };

  const handleColumnMappingChange = (field, value) => {
    setColumnMapping(prev => ({
      ...prev,
      [field]: parseInt(value)
    }));
  };

  const isValidMapping = () => {
    return columnMapping.productNumber >= 0 && columnMapping.price >= 0;
  };

  const processImport = () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    if (!isValidMapping()) {
      setError('Please map at least Product Number and Price columns');
      return;
    }

    setIsProcessing(true);
    setLoading(true);
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        const headers = jsonData[0] || [];
        const dataRows = jsonData.slice(1).filter(row => row.length > 0);
        
        if (dataRows.length === 0) {
          setError('No data found in the file');
          setLoading(false);
          setIsProcessing(false);
          return;
        }

        const newProducts = dataRows.map(row => {
          // Store ALL columns from the file in a rawData object
          const rawData = {};
          headers.forEach((header, index) => {
            if (header && row[index] !== undefined) {
              // Use header name as key, sanitize it for safety
              const key = String(header).trim();
              rawData[key] = row[index];
            }
          });
          
          return {
            id: generateUniqueId(),
            // Main mapped fields for display
            productNumber: row[columnMapping.productNumber] !== undefined ? String(row[columnMapping.productNumber]) : '',
            productName: columnMapping.productName >= 0 && row[columnMapping.productName] !== undefined ? String(row[columnMapping.productName]) : '',
            price: columnMapping.price >= 0 && row[columnMapping.price] !== undefined ? parseFloat(row[columnMapping.price]) || 0 : 0,
            category: columnMapping.category >= 0 && row[columnMapping.category] !== undefined ? String(row[columnMapping.category]) : '',
            // Store all original data for future use
            rawData: rawData,
            createdAt: new Date().toISOString(),
          };
        }).filter(p => p.productNumber); // Only keep products with a product number

        if (newProducts.length === 0) {
          setError('No valid items found in the file');
          setLoading(false);
          setIsProcessing(false);
          return;
        }

        // Merge with existing or replace
        const updatedProducts = [...products];
        newProducts.forEach(newProduct => {
          const existingIndex = updatedProducts.findIndex(p => p.productNumber === newProduct.productNumber);
          if (existingIndex >= 0) {
            updatedProducts[existingIndex] = { ...updatedProducts[existingIndex], ...newProduct };
          } else {
            updatedProducts.push(newProduct);
          }
        });

        setProducts(updatedProducts);
        saveProductsToOrganization(updatedProducts);
        
        // Reset import state
        setFile(null);
        setPreviewData(null);
        setShowColumnMapper(false);
        setActiveTab('catalog');
        showSuccessMessage(`Successfully imported ${newProducts.length} items`);
        
      } catch (error) {
        console.error('Error processing file:', error);
        setError('Error processing file. Please check the format.');
      } finally {
        setLoading(false);
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      setError('Error reading file');
      setLoading(false);
      setIsProcessing(false);
    };

    reader.readAsArrayBuffer(file);
  };

  // Manual entry handling
  const handleManualInputChange = (e) => {
    const { name, value } = e.target;
    setManualEntry(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addOrUpdateProduct = () => {
    if (!manualEntry.productNumber.trim()) {
      setError('Product number is required');
      return;
    }
    if (!manualEntry.price || parseFloat(manualEntry.price) < 0) {
      setError('Valid price is required');
      return;
    }

    let updatedProducts;
    
    if (editingProduct) {
      // Update existing product
      updatedProducts = products.map(p => 
        p.id === editingProduct.id 
          ? { 
              ...p, 
              ...manualEntry, 
              price: parseFloat(manualEntry.price) || 0,
              updatedAt: new Date().toISOString() 
            }
          : p
      );
      setEditingProduct(null);
      showSuccessMessage('Item updated successfully');
    } else {
      // Check if product number already exists
      const exists = products.some(p => p.productNumber === manualEntry.productNumber.trim());
      if (exists) {
        setError('An item with this number already exists');
        return;
      }
      
      // Add new product
      const newProduct = {
        id: generateUniqueId(),
        ...manualEntry,
        price: parseFloat(manualEntry.price) || 0,
        createdAt: new Date().toISOString(),
      };
      updatedProducts = [...products, newProduct];
      showSuccessMessage('Item added successfully');
    }

    setProducts(updatedProducts);
    saveProductsToOrganization(updatedProducts);
    setManualEntry({ productNumber: '', productName: '', price: '', category: '' });
    setError('');
  };

  const startEditing = (product) => {
    setEditingProduct(product);
    setManualEntry({
      productNumber: product.productNumber,
      productName: product.productName || '',
      price: String(product.price || ''),
      category: product.category || '',
    });
    setActiveTab('add');
  };

  const cancelEditing = () => {
    setEditingProduct(null);
    setManualEntry({ productNumber: '', productName: '', price: '', category: '' });
  };

  const deleteProduct = (productId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    const updatedProducts = products.filter(p => p.id !== productId);
    setProducts(updatedProducts);
    saveProductsToOrganization(updatedProducts);
    showSuccessMessage('Item deleted');
  };

  const clearAllProducts = () => {
    if (!window.confirm('Are you sure you want to delete ALL items? This cannot be undone.')) return;

    setProducts([]);
    saveProductsToOrganization([]);
    showSuccessMessage('All items deleted');
  };

  // Multi-select handlers
  const toggleSelect = (productId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const allSelected = filteredProducts.length > 0 && filteredProducts.every(p => prev.has(p.id));
      if (allSelected) return new Set();
      return new Set(filteredProducts.map(p => p.id));
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkDeleteSelected = () => {
    const count = selectedIds.size;
    if (count === 0) return;
    const updatedProducts = products.filter(p => !selectedIds.has(p.id));
    setProducts(updatedProducts);
    saveProductsToOrganization(updatedProducts);
    clearSelection();
    setShowBulkDeleteDialog(false);
    showSuccessMessage(`${count} item${count === 1 ? '' : 's'} deleted`);
  };

  // Sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Export
  const exportProducts = () => {
    const ws = XLSX.utils.json_to_sheet(products.map(p => ({
      'Item Number': p.productNumber,
      'Item Name': p.productName,
      'Price': p.price,
      'Category': p.category,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    XLSX.writeFile(wb, `item_catalog_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportProductsCSV = () => {
    const headers = ['Item Number', 'Item Name', 'Price', 'Category'];
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = products.map(p => [
      p.productNumber, p.productName, p.price, p.category
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `item_catalog_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="product-catalog-container">
      {/* Header */}
      <div className="catalog-header">
        <div className="header-content">
          <div className="header-icon">
            <AiOutlineShoppingCart />
          </div>
          <div className="header-text">
            <h1 data-help-title="Item Catalog" data-help="The master list of products or services you sell. Once your items are uploaded, LoyalBase can match incoming activity to a known item for category-based campaigns, reward redemption, and reporting.">Item Catalog</h1>
            <span className="item-count">{products.length} items</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-btn ${activeTab === 'catalog' ? 'active' : ''}`}
          onClick={() => setActiveTab('catalog')}
          data-help-title="View Catalog tab"
          data-help="Shows your full item catalog, where you can search, sort, export, and switch between table and grid views. Available to everyone."
        >
          <HiOutlineViewList />
          View Catalog
        </button>
        {isAdmin && (
          <>
            <button
              className={`tab-btn ${activeTab === 'import' ? 'active' : ''}`}
              onClick={() => setActiveTab('import')}
              data-help-title="Import Items tab"
              data-help="Opens the bulk import tool for uploading many items at once from an Excel (.xlsx, .xls) or CSV file. Admin only."
            >
              <HiOutlineUpload />
              Import Items
            </button>
            <button
              className={`tab-btn ${activeTab === 'add' ? 'active' : ''}`}
              onClick={() => { setActiveTab('add'); cancelEditing(); }}
              data-help-title="Add or edit item"
              data-help="Opens a form to add a single new item by hand. When you edit an item from the catalog, this tab becomes Edit Item. Admin only."
            >
              <HiOutlinePlus />
              {editingProduct ? 'Edit Item' : 'Add Item'}
            </button>
          </>
        )}
      </div>

      {/* Error & Success Messages */}
      {error && (
        <div className="message error-message">
          <HiOutlineExclamation />
          <span>{error}</span>
          <button onClick={() => setError('')}><HiOutlineX /></button>
        </div>
      )}
      {success && (
        <div className="message success-message">
          <HiOutlineCheck />
          <span>{success}</span>
        </div>
      )}

      {/* Tab Content */}
      <div className="tab-content">
        {/* Catalog View */}
        {activeTab === 'catalog' && (
          <div className="catalog-section">
            {/* Toolbar */}
            <div className="catalog-toolbar">
              <div className="search-box">
                <HiOutlineSearch />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-help-title="Search items"
                  data-help="Filters the catalog as you type by item number, item name, or category."
                />
              </div>
              <div className="toolbar-actions">
                <div className="view-toggle">
                  <button
                    className={viewMode === 'table' ? 'active' : ''}
                    onClick={() => setViewMode('table')}
                    title="Table View"
                    data-help-title="Table view"
                    data-help="Switches the catalog to a compact table with sortable columns."
                  >
                    <HiOutlineViewList />
                  </button>
                  <button
                    className={viewMode === 'grid' ? 'active' : ''}
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                    data-help-title="Grid view"
                    data-help="Switches the catalog to a grid of visual item cards."
                  >
                    <HiOutlineViewGrid />
                  </button>
                </div>
                {isAdmin && selectedIds.size > 0 && (
                  <div className="bulk-action-bar">
                    <span className="bulk-count">{selectedIds.size} selected</span>
                    <button className="action-btn danger-btn" onClick={() => setShowBulkDeleteDialog(true)} data-help-title="Delete selected" data-help="Permanently removes all selected items from the catalog after a confirmation dialog. This cannot be undone. Admin only.">
                      <HiOutlineTrash />
                      Delete Selected
                    </button>
                    <button className="action-btn" onClick={clearSelection} data-help-title="Clear selection" data-help="Deselects all currently selected items without deleting anything.">
                      <HiOutlineX />
                      Clear
                    </button>
                  </div>
                )}
                {products.length > 0 && (
                  <>
                    <button className="action-btn export-btn" onClick={exportProducts} data-help-title="Export XLSX" data-help="Downloads the entire catalog as an Excel file with item number, name, price, and category columns. The file name includes today's date.">
                      <HiOutlineDownload />
                      Export XLSX
                    </button>
                    <button className="action-btn export-btn" onClick={exportProductsCSV} data-help-title="Export CSV" data-help="Downloads the entire catalog as a CSV file with item number, name, price, and category columns. The file name includes today's date.">
                      <HiOutlineDownload />
                      Export CSV
                    </button>
                    {isAdmin && selectedIds.size === 0 && (
                      <button className="action-btn danger-btn" onClick={clearAllProducts} data-help-title="Clear all items" data-help="Deletes every item in the catalog after a confirmation. This is irreversible, so export a backup first if you might need the data. Admin only.">
                        <HiOutlineTrash />
                        Clear All
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Products Display */}
            {filteredProducts.length === 0 ? (
              <div className="empty-state">
                <BiPackage />
                <h3>{products.length === 0 ? 'No Items Yet' : 'No Results Found'}</h3>
                <p>
                  {products.length === 0 
                    ? 'Import items from a file or add them manually to get started.'
                    : 'Try adjusting your search criteria.'}
                </p>
                {isAdmin && products.length === 0 && (
                  <button className="cta-btn" onClick={() => setActiveTab('import')}>
                    <HiOutlineUpload /> Import Items
                  </button>
                )}
              </div>
            ) : viewMode === 'table' ? (
              <div className="products-table-wrapper">
                <table className="products-table" data-help-title="Item table" data-help="Lists every item matching your search. Clicking a column header sorts the catalog by that column; clicking again flips the sort direction. Checkboxes select items for bulk deletion.">
                  <thead>
                    <tr>
                      {isAdmin && (
                        <th className="select-col">
                          <input
                            type="checkbox"
                            aria-label="Select all items"
                            checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedIds.has(p.id))}
                            ref={el => {
                              if (el) {
                                const someSelected = filteredProducts.some(p => selectedIds.has(p.id));
                                const allSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedIds.has(p.id));
                                el.indeterminate = someSelected && !allSelected;
                              }
                            }}
                            onChange={toggleSelectAll}
                          />
                        </th>
                      )}
                      <th onClick={() => handleSort('productNumber')} className="sortable">
                        Item Number
                        {sortConfig.key === 'productNumber' && (
                          sortConfig.direction === 'asc' ? <HiOutlineSortAscending /> : <HiOutlineSortDescending />
                        )}
                      </th>
                      <th onClick={() => handleSort('productName')} className="sortable">
                        Item Name
                        {sortConfig.key === 'productName' && (
                          sortConfig.direction === 'asc' ? <HiOutlineSortAscending /> : <HiOutlineSortDescending />
                        )}
                      </th>
                      <th onClick={() => handleSort('price')} className="sortable">
                        Price
                        {sortConfig.key === 'price' && (
                          sortConfig.direction === 'asc' ? <HiOutlineSortAscending /> : <HiOutlineSortDescending />
                        )}
                      </th>
                      <th onClick={() => handleSort('category')} className="sortable">
                        Category
                        {sortConfig.key === 'category' && (
                          sortConfig.direction === 'asc' ? <HiOutlineSortAscending /> : <HiOutlineSortDescending />
                        )}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((product) => (
                      <tr key={product.id} className={selectedIds.has(product.id) ? 'row-selected' : ''}>
                        {isAdmin && (
                          <td className="select-col">
                            <input
                              type="checkbox"
                              aria-label={`Select ${product.productNumber}`}
                              checked={selectedIds.has(product.id)}
                              onChange={() => toggleSelect(product.id)}
                            />
                          </td>
                        )}
                        <td className="product-number">
                          <AiOutlineBarcode />
                          {product.productNumber}
                        </td>
                        <td>{product.productName || '—'}</td>
                        <td className="price">{formatCurrency(product.price)}</td>
                        <td>
                          {product.category ? (
                            <span className="category-badge">{product.category}</span>
                          ) : '—'}
                        </td>
                        <td className="actions">
                          {isAdmin && (
                            <>
                              <button className="edit-btn" onClick={() => startEditing(product)} title="Edit" data-help-title="Edit item" data-help="Opens this item in the Edit Item form, where you can change its name, price, or category. Admin only.">
                                <HiOutlinePencil />
                              </button>
                              <button className="delete-btn" onClick={() => deleteProduct(product.id)} title="Delete" data-help-title="Delete item" data-help="Permanently removes this item from the catalog after a confirmation. Admin only.">
                                <HiOutlineTrash />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="products-grid">
                {filteredProducts.map((product) => (
                  <div key={product.id} className={`product-card${selectedIds.has(product.id) ? ' selected' : ''}`}>
                    <div className="product-card-header">
                      <AiOutlineBarcode />
                      <span>{product.productNumber}</span>
                      {isAdmin && (
                        <input
                          type="checkbox"
                          className="card-select"
                          aria-label={`Select ${product.productNumber}`}
                          checked={selectedIds.has(product.id)}
                          onChange={() => toggleSelect(product.id)}
                        />
                      )}
                    </div>
                    <div className="product-card-body">
                      <h4>{product.productName || 'Unnamed Item'}</h4>
                      {product.category && (
                        <span className="category-badge">{product.category}</span>
                      )}
                    </div>
                    <div className="product-card-footer">
                      <span className="price">{formatCurrency(product.price)}</span>
                      <div className="card-actions">
                        {isAdmin && (
                          <>
                            <button onClick={() => startEditing(product)}><HiOutlinePencil /></button>
                            <button onClick={() => deleteProduct(product.id)}><HiOutlineTrash /></button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {filteredProducts.length > 0 && (
              <div className="results-count">
                Showing {filteredProducts.length} of {products.length} items
              </div>
            )}
          </div>
        )}

        {/* Import Section */}
        {activeTab === 'import' && (
          <div className="import-section">
            <div className="import-card">
              <div className="import-header">
                <HiOutlineUpload />
                <h3>Import Items from File</h3>
                <p>Upload an Excel (.xlsx, .xls) or CSV file containing your item data</p>
              </div>

              {/* Upload Area */}
              <div 
                className={`upload-zone ${file ? 'has-file' : ''}`}
                onClick={triggerFileInput}
                data-help-title="Upload file"
                data-help="Opens a file picker to choose an Excel (.xlsx, .xls) or CSV file of items; you can also drag and drop one here. Common column headers in English or Bulgarian are auto-detected and a preview of the first rows is shown."
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <HiOutlineDocumentText />
                {file ? (
                  <div className="file-info">
                    <strong>{file.name}</strong>
                    <span>{(file.size / 1024).toFixed(1)} KB</span>
                  </div>
                ) : (
                  <>
                    <p><strong>Click to upload</strong> or drag and drop</p>
                    <span>Excel or CSV files accepted</span>
                  </>
                )}
              </div>

              {/* Column Mapper */}
              {showColumnMapper && previewData && (
                <div className="column-mapper" data-help-title="Column mapping" data-help="Tells LoyalBase which spreadsheet column holds each catalog field. Item Number and Price are required; Item Name and Category are optional. Mapped columns are highlighted in the data preview below.">
                  <h4>Map Your Columns</h4>
                  <p>Select which column corresponds to each field. Item Number and Price are required.</p>
                  
                  <div className="mapping-grid">
                    <div className="mapping-field">
                      <label>
                        Item Number <span className="required">*</span>
                      </label>
                      <select
                        value={columnMapping.productNumber}
                        onChange={(e) => handleColumnMappingChange('productNumber', e.target.value)}
                      >
                        <option value="-1">Select column...</option>
                        {headerRow.map((header, idx) => (
                          <option key={idx} value={idx}>{header || `Column ${idx + 1}`}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="mapping-field">
                      <label>Item Name</label>
                      <select
                        value={columnMapping.productName}
                        onChange={(e) => handleColumnMappingChange('productName', e.target.value)}
                      >
                        <option value="-1">Select column...</option>
                        {headerRow.map((header, idx) => (
                          <option key={idx} value={idx}>{header || `Column ${idx + 1}`}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="mapping-field">
                      <label>
                        Price <span className="required">*</span>
                      </label>
                      <select
                        value={columnMapping.price}
                        onChange={(e) => handleColumnMappingChange('price', e.target.value)}
                      >
                        <option value="-1">Select column...</option>
                        {headerRow.map((header, idx) => (
                          <option key={idx} value={idx}>{header || `Column ${idx + 1}`}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="mapping-field">
                      <label>Category</label>
                      <select
                        value={columnMapping.category}
                        onChange={(e) => handleColumnMappingChange('category', e.target.value)}
                      >
                        <option value="-1">Select column...</option>
                        {headerRow.map((header, idx) => (
                          <option key={idx} value={idx}>{header || `Column ${idx + 1}`}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Preview Table */}
                  <div className="preview-section">
                    <h5>Data Preview</h5>
                    <div className="preview-table-wrapper">
                      <table className="preview-table">
                        <thead>
                          <tr>
                            {previewData.headers.map((header, idx) => (
                              <th 
                                key={idx}
                                className={
                                  idx === columnMapping.productNumber ||
                                  idx === columnMapping.productName ||
                                  idx === columnMapping.price ||
                                  idx === columnMapping.category
                                    ? 'highlighted'
                                    : ''
                                }
                              >
                                {header || `Col ${idx + 1}`}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.rows.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              {row.map((cell, cellIdx) => (
                                <td 
                                  key={cellIdx}
                                  className={
                                    cellIdx === columnMapping.productNumber ||
                                    cellIdx === columnMapping.productName ||
                                    cellIdx === columnMapping.price ||
                                    cellIdx === columnMapping.category
                                      ? 'highlighted'
                                      : ''
                                  }
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <button
                    className="import-btn"
                    onClick={processImport}
                    disabled={!isValidMapping() || isProcessing}
                    data-help-title="Import items"
                    data-help="Imports all rows from the file into the catalog. New items are added and existing items with the same item number are updated. Enabled once Item Number and Price columns are mapped."
                  >
                    {isProcessing ? (
                      <>
                        <span className="spinner"></span>
                        Importing...
                      </>
                    ) : (
                      <>
                        <HiOutlineUpload />
                        Import Items
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add/Edit Item Section */}
        {activeTab === 'add' && (
          <div className="add-section">
            <div className="add-card">
              <div className="add-header">
                {editingProduct ? <HiOutlinePencil /> : <HiOutlinePlus />}
                <h3>{editingProduct ? 'Edit Item' : 'Add New Item'}</h3>
                <p>{editingProduct ? 'Update the item details below' : 'Enter the item details to add it to your catalog'}</p>
              </div>

              <div className="form-grid" data-help-title="Item details" data-help="Holds the details of a single item. Item Number is the unique identifier and cannot be changed after creation; Price is required and shown in BGN. Name and category are optional.">
                <div className="form-group">
                  <label>
                    Item Number <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="productNumber"
                    value={manualEntry.productNumber}
                    onChange={handleManualInputChange}
                    placeholder="e.g., SKU-001, ITM-12345"
                    disabled={editingProduct !== null}
                  />
                </div>

                <div className="form-group">
                  <label>Item Name</label>
                  <input
                    type="text"
                    name="productName"
                    value={manualEntry.productName}
                    onChange={handleManualInputChange}
                    placeholder="e.g., Premium Widget"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Price <span className="required">*</span>
                  </label>
                  <div className="price-input">
                    <input
                      type="number"
                      name="price"
                      value={manualEntry.price}
                      onChange={handleManualInputChange}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                    <span className="currency">BGN</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    name="category"
                    value={manualEntry.category}
                    onChange={handleManualInputChange}
                    placeholder="e.g., Electronics, Clothing"
                  />
                </div>
              </div>

              <div className="form-actions">
                {editingProduct && (
                  <button className="cancel-btn" onClick={cancelEditing}>
                    Cancel
                  </button>
                )}
                <button
                  className="submit-btn"
                  onClick={addOrUpdateProduct}
                  disabled={!manualEntry.productNumber.trim() || !manualEntry.price}
                  data-help-title="Save item"
                  data-help="Adds the new item to the catalog, or saves your changes when editing an existing one. The catalog is saved immediately."
                >
                  {editingProduct ? (
                    <>
                      <HiOutlineCheck />
                      Update Item
                    </>
                  ) : (
                    <>
                      <HiOutlinePlus />
                      Add Item
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showBulkDeleteDialog && (
        <DeleteDialog
          title={`Delete ${selectedIds.size} item${selectedIds.size === 1 ? '' : 's'}?`}
          message="This will permanently remove the selected items from your catalog. This cannot be undone."
          yesFunction={bulkDeleteSelected}
          noFunction={() => setShowBulkDeleteDialog(false)}
        />
      )}
    </div>
  );
};

export default ProductCatalog;

